import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { inr, num, todayIST } from "../lib/format";
import { StickyBar, Field, PageHead } from "../components/FormBits";

const uid = () => String(Math.random()).slice(2);

const JobLine = ({ line, i, stock, onChange, onRemove }) => {
  const s = stock.find((x) => x.key === line.key);
  const expected = Number(line.consumption) > 0 ? Math.floor((Number(line.qty_issued) || 0) / Number(line.consumption) + 1e-9) : 0;
  const t = `job-line-${i}`;
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  return (
    <div className="panel rise space-y-3 p-4" data-testid={t}>
      <div className="flex items-center justify-between">
        <span className="font-heading font-bold">Fabric {i + 1}</span>
        <button type="button" className="icon-btn hover:!text-destructive" onClick={onRemove} aria-label="Remove line" data-testid={`${t}-remove-btn`}><Trash2 size={18} /></button>
      </div>
      <Field label="Fabric (from GRN stock) *">
        <select className="field" value={line.key} onChange={set("key")} data-testid={`${t}-fabric-select`}>
          <option value="">Select fabric…</option>
          {stock.filter((x) => x.available > 0 || x.key === line.key).map((x) => (
            <option key={x.key} value={x.key}>{x.description}{x.colour ? ` (${x.colour})` : ""}{x.code ? ` [${x.code}]` : ""} · {x.grn_no} · {num(x.available)} {x.uom} free</option>
          ))}
        </select>
      </Field>
      {s && <p className="text-xs text-muted-foreground" data-testid={`${t}-fabric-info`}>{s.vendor_name} · {s.po_no} · at {s.holder || "—"} · available {num(s.available)} {s.uom} · {inr(s.rate)}/{s.uom}</p>}
      <Field label="Style / make (e.g. Single pocket, full sleeve)">
        <input className="field" value={line.style} onChange={set("style")} data-testid={`${t}-style-input`} />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label={`Issued ${s?.uom || ""} *`}><input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={line.qty_issued} onChange={set("qty_issued")} data-testid={`${t}-qty-input`} /></Field>
        <Field label={`${s?.uom || "Mtr"} / shirt *`}><input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={line.consumption} onChange={set("consumption")} data-testid={`${t}-consumption-input`} /></Field>
        <Field label="Rate ₹ / shirt"><input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={line.rate} onChange={set("rate")} data-testid={`${t}-rate-input`} /></Field>
      </div>
      <p className="rounded-lg bg-muted/60 p-3 text-sm">Expected shirts: <b className="mono" data-testid={`${t}-expected`}>{num(expected)}</b></p>
    </div>
  );
};

export default function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fabs, setFabs] = useState([]);
  const [stock, setStock] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [f, s, j] = await Promise.all([api.get("/fabricators"), api.get("/fabric-stock", { params: { exclude_job: id } }),
        id ? api.get(`/jobs/${id}`) : Promise.resolve(null)]);
      setFabs(f.data);
      setStock(s.data);
      const job = j?.data;
      setForm(job ? { fabricator_id: job.fabricator.fabricator_id, issue_date: job.issue_date, due_date: job.due_date || "", remarks: job.remarks, job_no: job.job_no,
        items: job.items.map((it) => ({ ...it, uid: it.id, key: `${it.grn_id}:${it.po_item_id}` })) }
        : { fabricator_id: "", issue_date: todayIST(), due_date: "", remarks: "", items: [{ uid: uid(), key: "", style: "", qty_issued: "", consumption: "", rate: "" }] });
    };
    load().catch((e) => toast.error(errMsg(e)));
  }, [id]);

  if (!form) return <p className="text-muted-foreground">Loading…</p>;
  const setLine = (u, p) => setForm((f) => ({ ...f, items: f.items.map((l) => (l.uid === u ? { ...l, ...p } : l)) }));
  const totalIssued = form.items.reduce((a, l) => a + (Number(l.qty_issued) || 0), 0);

  const save = async () => {
    if (!form.fabricator_id) return toast.error("Please select a fabricator");
    const bad = form.items.find((l) => !l.key || !(Number(l.qty_issued) > 0) || !(Number(l.consumption) > 0));
    if (!form.items.length || bad) return toast.error("Each line needs fabric, issued quantity and quantity per shirt");
    setSaving(true);
    const body = { fabricator_id: form.fabricator_id, issue_date: form.issue_date, due_date: form.due_date || null, remarks: form.remarks,
      items: form.items.map((l) => { const [grn_id, po_item_id] = l.key.split(":");
        return { id: l.id, grn_id, po_item_id, style: l.style, qty_issued: Number(l.qty_issued), consumption: Number(l.consumption), rate: Number(l.rate) || 0 }; }) };
    try {
      const { data } = id ? await api.put(`/jobs/${id}`, body) : await api.post("/jobs", body);
      toast.success(`${data.job_no} saved`);
      navigate("/jobs", { state: { open: data.id } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="job-form">
      <PageHead kicker={id ? `Editing ${form.job_no}` : "Fabric issue to fabricator"} title={id ? "Edit job order" : "New job order"} />
      <section className="panel rise space-y-4 p-4">
        <Field label="Fabricator *">
          <select className="field" value={form.fabricator_id} onChange={(e) => setForm({ ...form, fabricator_id: e.target.value })} data-testid="job-fabricator-select">
            <option value="">Select fabricator…</option>
            {fabs.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.code})</option>)}
          </select>
          {!fabs.length && <p className="mt-2 text-sm text-muted-foreground">No fabricators yet — add one in Setup → Fabricators.</p>}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Issue date"><input type="date" className="field" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="job-date-input" /></Field>
          <Field label="Due date"><input type="date" className="field" min={form.issue_date} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="job-due-date-input" /></Field>
        </div>
      </section>
      {!stock.length && <p className="panel p-4 text-sm text-muted-foreground" data-testid="job-no-stock">No fabric in stock yet. Fabric appears here after a GRN records it as received.</p>}
      {form.items.map((l, i) => (
        <JobLine key={l.uid} line={l} i={i} stock={stock.filter((x) => !x.fabricator_id || x.fabricator_id === form.fabricator_id)} onChange={(p) => setLine(l.uid, p)}
          onRemove={() => setForm((f) => ({ ...f, items: f.items.filter((x) => x.uid !== l.uid) }))} />
      ))}
      <button type="button" className="btn btn-outline w-full border-dashed" data-testid="job-add-line-btn"
        onClick={() => setForm({ ...form, items: [...form.items, { uid: uid(), key: "", style: "", qty_issued: "", consumption: "", rate: "" }] })}>
        <Plus size={18} /> Add fabric
      </button>
      <section className="panel p-4">
        <Field label="Remarks"><textarea className="field" rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="job-remarks-input" /></Field>
      </section>
      <StickyBar onSave={save} saving={saving} label={id ? "Update job" : "Save job"} testid="job">
        <p className="mono-label text-muted-foreground">Fabric issued</p>
        <p className="mono truncate text-xl font-bold" data-testid="job-total-issued">{num(totalIssued)}</p>
      </StickyBar>
    </div>
  );
}
